import type { MetaDescriptor } from 'react-router';

import type { FaqItem } from '@/components/sections/faq-section';
import logoUrl from '@/assets/logos/logo.png';

// LdJsonObject/LdJsonValue aren't exported from react-router's public API
// (only the MetaDescriptor union that references them is) — hand-rolled here
// to stay structurally compatible with the "script:ld+json" descriptor.
export type JsonLdPrimitive = string | number | boolean | null;
export type JsonLdValue = JsonLdPrimitive | JsonLdObject | Array<JsonLdValue>;
export type JsonLdObject = { [key: string]: JsonLdValue };

export const SITE_URL = 'https://meetrealestate.com.au';

export function absoluteUrl(path: string): string {
  return new URL(path, SITE_URL).toString();
}

export function ldJsonMeta(schema: JsonLdObject): { 'script:ld+json': JsonLdObject } {
  return { 'script:ld+json': schema };
}

/**
 * React Router doesn't merge meta() arrays across the route tree — a leaf
 * route's meta() fully replaces its ancestors'. Call this from a leaf route's
 * meta({ matches }) to pull the root's JSON-LD back in alongside its own.
 */
export function inheritRootLdJson(
  matches: ReadonlyArray<{ meta?: Array<MetaDescriptor> } | undefined>,
): Array<MetaDescriptor> {
  const rootMeta = matches[0]?.meta ?? [];
  return rootMeta.filter((entry) => Boolean(entry) && 'script:ld+json' in entry);
}

export function buildRealEstateAgentSchema(): JsonLdObject {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: 'Meet Real Estate',
    description:
      "Meet Real Estate is a boutique property advisory serving Kingaroy and the South Burnett, built on 50 years of family heritage and 26 years of global real estate expertise. We turn up, when others don't.",
    url: SITE_URL,
    logo: absoluteUrl(logoUrl),
    image: absoluteUrl(logoUrl),
    telephone: '+61 493 035 628',
    email: 'lucio@meetrealestate.com.au',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Kingaroy',
      addressRegion: 'QLD',
      postalCode: '4610',
      addressCountry: 'AU',
    },
    areaServed: [
      { '@type': 'Place', name: 'Kingaroy' },
      { '@type': 'Place', name: 'South Burnett' },
      { '@type': 'State', name: 'Queensland' },
    ],
    taxID: '49445878682',
    founder: { '@type': 'Person', name: 'Lucio Passos', jobTitle: 'Sales Agent' },
  };
}

export function buildFaqPageSchema(faqs: Array<FaqItem>): JsonLdObject {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
}
